import { useState, useMemo } from 'react';
import { spreadExamples } from '../data';

interface PriceParts {
  integer: string;
  decimal: string;
  extra: string;
}

function formatPriceParts(value: number, pair: string): PriceParts {
  const isJpy = pair.includes('JPY') || pair === 'XAUUSD';
  const isGold = pair === 'XAUUSD';
  const digits = isGold ? 2 : isJpy ? 3 : 5;
  const formatted = value.toFixed(digits);
  const [intPart, decPart] = formatted.split('.');

  if (isGold) {
    return { integer: intPart, decimal: `.${decPart}`, extra: '' };
  }

  if (isJpy) {
    const small = decPart.slice(0, 2);
    const last = decPart.slice(2, 3);
    return { integer: intPart, decimal: `.${small}`, extra: last };
  }

  const small = decPart.slice(0, 2);
  const last = decPart.slice(2, 5);
  return { integer: intPart, decimal: `.${small}`, extra: last };
}

function formatPriceFull(value: number, pair: string): string {
  const isJpy = pair.includes('JPY') || pair === 'XAUUSD';
  const isGold = pair === 'XAUUSD';
  if (isGold) return value.toFixed(2);
  if (isJpy) return value.toFixed(3);
  return value.toFixed(5);
}

export default function BidAskSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFlashing, setIsFlashing] = useState(false);
  const current = spreadExamples[activeIndex];
  const spread = current.ask - current.bid;

  const handleSwitch = (index: number) => {
    if (index === activeIndex) return;
    setIsFlashing(true);
    setActiveIndex(index);
    setTimeout(() => setIsFlashing(false), 350);
  };

  const bidParts = useMemo(() => formatPriceParts(current.bid, current.pair), [current]);
  const askParts = useMemo(() => formatPriceParts(current.ask, current.pair), [current]);
  const bidFull = formatPriceFull(current.bid, current.pair);
  const askFull = formatPriceFull(current.ask, current.pair);
  const spreadPips = current.pair === 'XAUUSD'
    ? (spread * 10).toFixed(1)
    : (spread * 10000).toFixed(1);
  const isJpy = current.pair.includes('JPY') || current.pair === 'XAUUSD';

  const spreadVisualWidth = Math.min(Math.max((spread * 10000) / 15, 2), 48);
  const pipCost = (parseFloat(spreadPips) * 10).toFixed(2);

  return (
    <section id="bid-ask-spread" className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[640px] mb-8 md:mb-14">
          <h2 className="text-[28px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-3 md:mb-4">
            How bid and ask
            <br />
            form the spread
          </h2>
          <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-600">
            Every quote has two prices: one for buying, one for selling. The gap between them is what you pay before the trade even moves.
          </p>
        </div>

        {/* ── MOBILE LAYOUT (< lg) ── */}
        <div className="lg:hidden flex flex-col gap-4">
          {/* Pair Selector — horizontal scroll chips */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-4 px-4">
            {spreadExamples.map((item, i) => {
              const isActive = i === activeIndex;
              const s = item.ask - item.bid;
              const sp = item.pair === 'XAUUSD'
                ? (s * 10).toFixed(1)
                : (s * 10000).toFixed(1);
              return (
                <button
                  key={item.pair}
                  onClick={() => handleSwitch(i)}
                  className={`flex-shrink-0 flex flex-col items-center px-4 py-2.5 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-white border-background-200 text-foreground-700 hover:border-background-300'
                  }`}
                >
                  <span className="text-[13px] font-bold leading-tight">{item.pair}</span>
                  <span className={`text-[11px] font-semibold leading-tight ${isActive ? 'text-white/80' : 'text-foreground-400'}`}>
                    {sp}p
                  </span>
                </button>
              );
            })}
          </div>

          {/* Terminal card */}
          <div className="bg-foreground-950 rounded-[20px] overflow-hidden">
            {/* Terminal header bar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-foreground-800/60">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-500" />
                </span>
                <span className="text-[11px] font-bold text-accent-500 tracking-wider uppercase">Live Quote</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-bold text-foreground-100">{current.pair}</span>
                <span className="text-[10px] font-semibold text-foreground-500 bg-foreground-900 px-2 py-0.5 rounded-full">
                  {current.label}
                </span>
              </div>
            </div>

            {/* Bid row */}
            <div className={`px-4 pt-4 pb-3 transition-all duration-300 ${isFlashing ? 'opacity-50' : 'opacity-100'}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-accent-500">BID</span>
                  <span className="text-[10px] text-foreground-500 uppercase">Sell</span>
                </div>
                <span className="text-[12px] text-foreground-500 tabular-nums">{bidFull}</span>
              </div>
              <div className="font-[750] text-foreground-50 leading-none tracking-tight tabular-nums">
                <span className="text-[42px]">{bidParts.integer}</span>
                <span className="text-[30px] text-foreground-300">{bidParts.decimal}</span>
                {bidParts.extra && (
                  <span className="text-[20px] text-foreground-500 align-top relative -top-1">{bidParts.extra}</span>
                )}
              </div>
            </div>

            {/* Gap divider */}
            <div className="mx-4 flex items-center gap-3 py-2">
              <div className="flex-1 h-[1px] bg-foreground-800" />
              <div className="flex items-center gap-2 bg-primary-500/15 border border-primary-500/30 rounded-full px-3 py-1">
                <i className="ri-expand-up-down-line text-[12px] text-primary-400" />
                <span className="text-[13px] font-bold text-primary-400 tabular-nums">{spreadPips}</span>
                <span className="text-[10px] text-primary-500/70 font-medium">pips spread</span>
              </div>
              <div className="flex-1 h-[1px] bg-foreground-800" />
            </div>

            {/* Ask row */}
            <div className={`px-4 pt-1 pb-4 transition-all duration-300 ${isFlashing ? 'opacity-50' : 'opacity-100'}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-secondary-400">ASK</span>
                  <span className="text-[10px] text-foreground-500 uppercase">Buy</span>
                </div>
                <span className="text-[12px] text-foreground-500 tabular-nums">{askFull}</span>
              </div>
              <div className="font-[750] text-foreground-50 leading-none tracking-tight tabular-nums">
                <span className="text-[42px]">{askParts.integer}</span>
                <span className="text-[30px] text-foreground-300">{askParts.decimal}</span>
                {askParts.extra && (
                  <span className="text-[20px] text-foreground-500 align-top relative -top-1">{askParts.extra}</span>
                )}
              </div>
            </div>

            {/* Spread bar simplified */}
            <div className="mx-4 mb-4 bg-foreground-900/60 rounded-[12px] border border-foreground-800/50 p-3">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide">Spread Visualization</span>
                <span className="text-[10px] text-foreground-600">{isJpy ? '3 decimals' : '5 decimals'}</span>
              </div>
              <div className="h-[28px] bg-foreground-900 rounded-[6px] relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-[35%] bg-accent-500/10 border-r border-accent-500/20 flex items-center pl-2.5">
                  <span className="text-[9px] font-bold text-accent-500/70 uppercase tracking-wider">Bid</span>
                </div>
                <div
                  className="absolute top-0 bottom-0 bg-primary-500/25 border-x border-primary-500/40 flex items-center justify-center transition-all duration-500"
                  style={{ left: `${42 - spreadVisualWidth / 2}%`, width: `${spreadVisualWidth}%` }}
                >
                  <span className="text-[8px] font-bold text-primary-400 whitespace-nowrap">GAP</span>
                </div>
                <div className="absolute right-0 top-0 bottom-0 w-[35%] bg-secondary-500/10 border-l border-secondary-500/20 flex items-center justify-end pr-2.5">
                  <span className="text-[9px] font-bold text-secondary-500/70 uppercase tracking-wider">Ask</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] font-bold text-accent-400 tabular-nums">{bidFull}</span>
                <span className="text-[11px] font-bold text-primary-400 tabular-nums">{spread.toFixed(isJpy ? 3 : 5)}</span>
                <span className="text-[11px] font-bold text-secondary-400 tabular-nums">{askFull}</span>
              </div>
            </div>

            {/* Cost pill */}
            <div className="mx-4 mb-4 flex items-center justify-between bg-foreground-900/40 rounded-[12px] border border-foreground-800/40 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-[8px] bg-primary-500/15 flex items-center justify-center flex-shrink-0">
                  <i className="ri-price-tag-3-line text-[14px] text-primary-400" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-foreground-300">Cost per standard lot</div>
                  <div className="text-[11px] text-foreground-500">{spreadPips} pips &times; $10/pip</div>
                </div>
              </div>
              <div className="text-[20px] font-bold text-primary-400 tabular-nums">${pipCost}</div>
            </div>
          </div>

          {/* Tip card */}
          <div className="bg-secondary-50 rounded-[16px] border border-secondary-200/60 px-4 py-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-[10px] bg-secondary-500/15 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-line text-[16px] text-secondary-500" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-foreground-900 mb-0.5">You always pay spread</div>
                <div className="text-[13px] text-foreground-600 leading-relaxed">
                  Buy at the ask, sell at the bid. The spread is collected the moment you open a position — before the market moves a single pip.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── DESKTOP LAYOUT (lg+) ── */}
        <div className="hidden lg:grid grid-cols-5 gap-8">
          {/* Left: Quote Terminal */}
          <div className="col-span-3 bg-foreground-950 rounded-card p-7 flex flex-col gap-5 min-h-[480px]">
            {/* Terminal Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent-500" />
                </span>
                <span className="text-[12px] font-bold text-accent-500 tracking-wider uppercase">Live</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-bold text-foreground-100 tracking-tight">{current.pair}</span>
                <span className="text-[11px] font-semibold text-foreground-500 bg-foreground-900 px-2 py-0.5 rounded-full">
                  {current.label}
                </span>
              </div>
            </div>

            {/* Price Cards Row */}
            <div className="flex items-stretch gap-3 mt-2">
              {/* Bid Card */}
              <div className={`flex-1 bg-foreground-900/60 rounded-[14px] border border-accent-500/25 p-5 flex flex-col gap-3 transition-all duration-300 ${isFlashing ? 'opacity-60' : 'opacity-100'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-accent-500/80 tracking-wider uppercase">Bid</span>
                  <span className="text-[10px] font-medium text-foreground-500 uppercase">Sell</span>
                </div>
                <div className="font-[750] text-foreground-100 leading-none tracking-tight tabular-nums">
                  <span className="text-[38px]">{bidParts.integer}</span>
                  <span className="text-[28px] text-foreground-400">{bidParts.decimal}</span>
                  {bidParts.extra && (
                    <span className="text-[18px] text-foreground-500 align-top relative -top-2">{bidParts.extra}</span>
                  )}
                </div>
                <div className="text-[11px] text-foreground-500 font-medium">{bidFull}</div>
              </div>

              {/* Spread Gap Indicator */}
              <div className="flex flex-col items-center justify-center gap-1 min-w-[72px]">
                <div className="text-[11px] font-bold text-foreground-500 tracking-wider uppercase">Gap</div>
                <div className="relative flex-1 w-full flex items-center">
                  <div className="w-full h-[3px] bg-foreground-800 rounded-full overflow-hidden relative">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all duration-500"
                      style={{ width: `${spreadVisualWidth}%`, marginLeft: `${(100 - spreadVisualWidth) / 2}%` }}
                    />
                  </div>
                </div>
                <div className="text-[18px] font-bold text-primary-500 tabular-nums">{spreadPips}</div>
                <div className="text-[10px] font-medium text-foreground-500">pips</div>
              </div>

              {/* Ask Card */}
              <div className={`flex-1 bg-foreground-900/60 rounded-[14px] border border-secondary-500/25 p-5 flex flex-col gap-3 transition-all duration-300 ${isFlashing ? 'opacity-60' : 'opacity-100'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-secondary-500/80 tracking-wider uppercase">Ask</span>
                  <span className="text-[10px] font-medium text-foreground-500 uppercase">Buy</span>
                </div>
                <div className="font-[750] text-foreground-100 leading-none tracking-tight tabular-nums">
                  <span className="text-[38px]">{askParts.integer}</span>
                  <span className="text-[28px] text-foreground-400">{askParts.decimal}</span>
                  {askParts.extra && (
                    <span className="text-[18px] text-foreground-500 align-top relative -top-2">{askParts.extra}</span>
                  )}
                </div>
                <div className="text-[11px] text-foreground-500 font-medium">{askFull}</div>
              </div>
            </div>

            {/* Detailed Spread Bar */}
            <div className="bg-foreground-900/40 rounded-[14px] border border-foreground-800/60 p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[12px] font-semibold text-foreground-400">Spread Visualization</span>
                <span className="text-[11px] text-foreground-500">{isJpy ? '3 decimals' : '5 decimals'}</span>
              </div>
              <div className="relative">
                <div className="h-[32px] bg-foreground-900 rounded-[8px] relative overflow-hidden">
                  <div className="absolute left-0 top-0 bottom-0 w-[35%] bg-accent-500/12 border-r border-accent-500/20 flex items-center pl-3">
                    <span className="text-[10px] font-bold text-accent-500/70 uppercase tracking-wider">Bid</span>
                  </div>
                  <div className="absolute top-0 bottom-0 bg-primary-500/20 border-x border-primary-500/30 flex items-center justify-center transition-all duration-500"
                    style={{ left: `${42 - spreadVisualWidth / 2}%`, width: `${spreadVisualWidth}%` }}
                  >
                    <span className="text-[10px] font-bold text-primary-500 whitespace-nowrap">SPREAD</span>
                  </div>
                  <div className="absolute right-0 top-0 bottom-0 w-[35%] bg-secondary-500/12 border-l border-secondary-500/20 flex items-center justify-end pr-3">
                    <span className="text-[10px] font-bold text-secondary-500/70 uppercase tracking-wider">Ask</span>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[13px] font-bold text-accent-500 tabular-nums">{bidFull}</span>
                  <span className="text-[12px] font-bold text-primary-500 tabular-nums">{spread.toFixed(isJpy ? 3 : 5)}</span>
                  <span className="text-[13px] font-bold text-secondary-500 tabular-nums">{askFull}</span>
                </div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="mt-auto flex items-center gap-3 bg-foreground-900/40 rounded-[12px] border border-foreground-800/50 px-4 py-3.5">
              <div className="w-8 h-8 rounded-[10px] bg-primary-500/15 flex items-center justify-center flex-shrink-0">
                <i className="ri-price-tag-3-line text-[16px] text-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-foreground-200 mb-0.5">
                  Spread cost per standard lot
                </div>
                <div className="text-[12px] text-foreground-500">
                  {spreadPips} pips &times; $10/pip = <span className="font-bold text-foreground-200">${pipCost}</span> per trade
                </div>
              </div>
              <div className="text-[18px] font-bold text-primary-500 tabular-nums flex-shrink-0">
                ${pipCost}
              </div>
            </div>
          </div>

          {/* Right: Pair Selector */}
          <div className="col-span-2 flex flex-col gap-3">
            <div className="text-[13px] font-semibold text-foreground-500 uppercase tracking-wide mb-1 pl-1">
              Select pair
            </div>
            {spreadExamples.map((item, i) => {
              const isActive = i === activeIndex;
              const s = item.ask - item.bid;
              const sp = item.pair === 'XAUUSD'
                ? (s * 10).toFixed(1)
                : (s * 10000).toFixed(1);
              return (
                <button
                  key={item.pair}
                  onClick={() => handleSwitch(i)}
                  className={`text-left w-full px-4 py-3.5 rounded-card-sm border transition-all duration-250 cursor-pointer hover:-translate-y-0.5 ${
                    isActive
                      ? 'bg-primary-50 border-primary-300 shadow-sm shadow-primary-500/5'
                      : 'bg-white border-background-200 hover:border-background-300 hover:bg-background-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-[3px] h-10 rounded-full flex-shrink-0 transition-colors duration-250 ${
                      isActive ? 'bg-primary-500' : 'bg-background-200'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[15px] font-bold ${isActive ? 'text-primary-700' : 'text-foreground-950'}`}>
                          {item.pair}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            isActive ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'
                          }`}>
                            {item.label}
                          </span>
                          <span className={`text-[12px] font-bold tabular-nums ${isActive ? 'text-primary-600' : 'text-foreground-500'}`}>
                            {sp}p
                          </span>
                        </div>
                      </div>
                      <p className="text-[13px] text-foreground-500 leading-snug truncate">
                        {item.description}
                      </p>
                    </div>
                    <i className={`ri-arrow-right-s-line text-[18px] flex-shrink-0 transition-colors duration-250 ${
                      isActive ? 'text-primary-500' : 'text-foreground-300'
                    }`} />
                  </div>
                </button>
              );
            })}

            {/* Tip Card */}
            <div className="bg-secondary-50 rounded-card-sm border border-secondary-200/60 px-4 py-4 mt-1 hover:-translate-y-0.5 transition-transform duration-200">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-[10px] bg-secondary-500/15 flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-line text-[16px] text-secondary-500" />
                </div>
                <div>
                  <div className="text-[14px] font-semibold text-foreground-900 mb-0.5">You always pay spread</div>
                  <div className="text-[13px] text-foreground-600 leading-relaxed">
                    Buy at the ask, sell at the bid. The spread is collected the moment you open a position — before the market moves a single pip.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}